import { useState } from 'react';
import { ChevronRight, ImagePlus } from 'lucide-react';
import type { FluxogramaShape } from '@/types/fluxograma';
import { SHAPE_CATEGORIES, SHAPES_BY_CATEGORY, SHAPE_META } from './shapeMeta';
import { ShapeRenderer } from './ShapeRenderer';

interface ShapesPaletteProps {
  onDragStart: (shape: FluxogramaShape) => void;
}

export function ShapesPalette({ onDragStart }: ShapesPaletteProps) {
  const [openCats, setOpenCats] = useState<Record<string, boolean>>({
    ansi: true,
    bpmn: true,
    anotacao: true,
    figuras: true,
  });

  return (
    <aside className="w-56 border-r bg-background overflow-y-auto">
      <div className="px-3 py-2 border-b text-base font-semibold text-muted-foreground uppercase tracking-wide">
        Formas
      </div>
      {SHAPE_CATEGORIES.map((cat) => {
        const open = !!openCats[cat.key];
        return (
          <div key={cat.key} className="border-b">
            <button
              type="button"
              className="w-full flex items-center justify-between px-3 py-2 text-xs font-medium hover:bg-muted"
              onClick={() => setOpenCats((p) => ({ ...p, [cat.key]: !p[cat.key] }))}
            >
              <span>{cat.label}</span>
              <ChevronRight
                className={`h-4 w-4 transition-transform ${open ? 'rotate-90' : ''}`}
              />
            </button>
            {open && (
              <div className="grid grid-cols-2 gap-2 p-2">
                {SHAPES_BY_CATEGORY(cat.key).map((meta) => (
                  <PaletteItem
                    key={meta.shape}
                    shape={meta.shape}
                    label={meta.label}
                    color={meta.defaultColor}
                    onDragStart={onDragStart}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
      <div className="px-3 py-3 text-[11px] text-muted-foreground">
        Arraste uma forma para o canvas.
      </div>
    </aside>
  );
}

interface PaletteItemProps {
  shape: FluxogramaShape;
  label: string;
  color: string;
  onDragStart: (shape: FluxogramaShape) => void;
}

function PaletteItem({ shape, label, color, onDragStart }: PaletteItemProps) {
  const meta = SHAPE_META[shape];
  return (
    <div
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData('application/x-fluxograma-shape', shape);
        e.dataTransfer.effectAllowed = 'copy';
        onDragStart(shape);
      }}
      className="cursor-grab active:cursor-grabbing border rounded-md p-2 bg-card hover:border-primary/60 hover:shadow-sm transition flex flex-col items-center gap-1"
      title={label}
    >
      <div className="w-12 h-10 pointer-events-none flex items-center justify-center">
        {shape === 'figura' ? (
          <ImagePlus className="h-7 w-7" style={{ color }} />
        ) : (
          <ShapeRenderer
            shape={shape}
            width={meta.defaultWidth}
            height={meta.defaultHeight}
            bgColor="#ffffff"
            borderColor={color}
            borderWidth={2}
          />
        )}
      </div>
      <div className="text-[10px] text-center leading-tight text-muted-foreground line-clamp-2">
        {label}
      </div>
    </div>
  );
}
